import { useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  NavbarItem,
} from '@accure/ui';
import { EllipsisVertical } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDatetime } from '@/utils/format/date';

import { type User } from '../entities/user';
import { GetUsersQuery } from '../queries/get-users';

import { UsersTableSkeletonRow } from './users-table-skeleton-row';

type UserTableProps = {
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
};

const pageSize = 10;

export function UserTable({ onEditUser, onDeleteUser }: UserTableProps) {
  const [page, setPage] = useState<number>(1);
  const usersQuery = GetUsersQuery.useQuery({ page, pageSize });

  function handlePreviousPage() {
    if (typeof usersQuery.data?.previousPage === 'number') {
      setPage(usersQuery.data.previousPage);
    }
  }

  function handleNextPage() {
    if (typeof usersQuery.data?.nextPage === 'number') {
      setPage(usersQuery.data.nextPage);
    }
  }

  if (usersQuery.isError) {
    throw usersQuery.error;
  }

  return (
    <>
      <Table className="w-full table-auto caption-bottom text-sm">
        <TableHead>
          <TableRow className="border-b transition-colors hover:bg-muted/50">
            <TableHeader className="h-10 !px-4 py-2 align-middle font-medium text-muted-foreground">
              Name
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Email
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Status
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Role
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Last Login
            </TableHeader>
            <TableHeader className="size-10 px-4 text-left align-middle font-medium text-muted-foreground">
              <span className="sr-only">Actions</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="transition-all duration-300 ease-in-out">
          {usersQuery.isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <UsersTableSkeletonRow key={index} />
            ))
          ) : usersQuery.data ? (
            usersQuery.data.users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="p-8 text-center text-muted-foreground"
                >
                  No users available.
                </TableCell>
              </TableRow>
            ) : (
              usersQuery.data.users.map((user) => (
                <TableRow
                  key={user.id}
                  className="animate-in fade-in cursor-pointer border-b transition-colors duration-200 hover:bg-muted/50"
                >
                  <TableCell className="!px-4 py-2 align-middle">
                    <div className="truncate font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <div className="truncate text-gray-600">{user.email}</div>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <Badge
                      className={
                        user.active
                          ? 'bg-primary-500'
                          : 'border-transparent bg-neutral-100 text-neutral-900 [a&]:hover:bg-neutral-100/90'
                      }
                    >
                      {user.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <Badge variant="outline" className="text-gray-700">
                      {user.role.name}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <div className="truncate text-sm text-gray-600">
                      {user.lastLogin ? formatDatetime(user.lastLogin) : '-'}
                    </div>
                  </TableCell>
                  <TableCell className="p-2 text-center align-middle">
                    <Dropdown>
                      <DropdownButton
                        as={NavbarItem}
                        className="size-8 border-0 bg-transparent p-0 hover:bg-gray-100"
                      >
                        <EllipsisVertical className="size-4" />
                        <span className="sr-only">Open menu</span>
                      </DropdownButton>
                      <DropdownMenu
                        className="absolute right-0 z-50 min-w-48 rounded border bg-white shadow-lg"
                        anchor="top"
                      >
                        <DropdownItem
                          className="transition-colors hover:bg-gray-50"
                          onClick={() => onEditUser(user)}
                        >
                          <DropdownLabel>Edit</DropdownLabel>
                        </DropdownItem>
                        <DropdownDivider />
                        <DropdownItem
                          className="text-red-600 transition-colors hover:bg-red-50"
                          onClick={() => onDeleteUser(user)}
                        >
                          <DropdownLabel>Delete</DropdownLabel>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              ))
            )
          ) : null}
        </TableBody>
      </Table>

      {usersQuery.data &&
      (typeof usersQuery.data.nextPage === 'number' ||
        typeof usersQuery.data.previousPage === 'number') ? (
        <nav className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div className="hidden sm:block">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">
                {Math.max((usersQuery.data.currentPage - 1) * pageSize + 1, 1)}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(
                  usersQuery.data.currentPage * pageSize,
                  usersQuery.data.totalCount,
                )}
              </span>{' '}
              of{' '}
              <span className="font-medium">{usersQuery.data.totalCount}</span>{' '}
              users
            </p>
          </div>
          <div className="flex flex-1 justify-between gap-2 sm:justify-end">
            <Button
              variant="ghost"
              disabled={typeof usersQuery.data.previousPage !== 'number'}
              onClick={handlePreviousPage}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              disabled={typeof usersQuery.data.nextPage !== 'number'}
              onClick={handleNextPage}
            >
              Next
            </Button>
          </div>
        </nav>
      ) : null}
    </>
  );
}
